import type { Option } from '@/modules/common/types/option.type.ts';
import { Operator } from '@/modules/viewer/enums/operator.enum.ts';
import { ElementType } from '@/modules/viewer/enums/element-type.enum.ts';
import { CURRENT_OPERATOR } from '@/modules/viewer/consts/current-operator.const.ts';

export class OperatorUtil {
  private static readonly VALUELESS_OPERATORS = [
    Operator.Empty,
    Operator.NotEmpty,
    Operator.Today,
    Operator.Yesterday,
    Operator.Last7Days,
    Operator.Last30Days,
    Operator.True,
    Operator.False,
  ];

  static isValueless(operator: Operator) {
    return OperatorUtil.VALUELESS_OPERATORS.includes(operator);
  }

  private static readonly LABELS: Record<Operator, string | null> = {
    [Operator.NotEmpty]: 'viewer.filter.operator.notEmpty',
    [Operator.Empty]: 'viewer.filter.operator.empty',
    [Operator.Contains]: 'viewer.filter.operator.contains',
    [Operator.NotContains]: 'viewer.filter.operator.notContains',
    [Operator.StartsWith]: 'viewer.filter.operator.startsWith',
    [Operator.EndsWith]: 'viewer.filter.operator.endWith',
    [Operator.NotEquals]: 'viewer.filter.operator.notQuals',
    [Operator.Greater]: 'viewer.filter.operator.greater',
    [Operator.GreaterOrEqual]: 'viewer.filter.operator.greaterOrEqual',
    [Operator.Less]: 'viewer.filter.operator.less',
    [Operator.LessOrEqual]: 'viewer.filter.operator.lessOrEqual',
    [Operator.Range]: 'viewer.filter.operator.range',
    [Operator.Today]: 'viewer.filter.operator.today',
    [Operator.Yesterday]: 'viewer.filter.operator.yesterday',
    [Operator.Last7Days]: 'viewer.filter.operator.last7Days',
    [Operator.Last30Days]: 'viewer.filter.operator.last30Days',
    [Operator.True]: 'viewer.filter.operator.true',
    [Operator.False]: 'viewer.filter.operator.false',
    [Operator.In]: 'viewer.filter.operator.in',
    [Operator.NotIn]: 'viewer.filter.operator.notIn',
    [Operator.Equals]: null, // "Соответствует" или "Равен" в зависимости от типа колонки
  };

  static getLabel(operator: Operator, isNumber = false) {
    const label = OperatorUtil.LABELS[operator];

    if (label) {
      return label;
    }

    return isNumber ? 'viewer.filter.operator.equals' : 'viewer.filter.operator.matches';
  }

  static getOption<O extends Operator>(operator: O, isNumber = false): Option<O> {
    return {
      code: operator,
      label: OperatorUtil.getLabel(operator, isNumber),
    };
  }

  static getOptions<O extends Operator>(operators: O[], isNumber = false) {
    return operators.map((operator) => OperatorUtil.getOption(operator, isNumber));
  }

  private static readonly OPTIONS: Record<ElementType, Option<Operator>[]> = {
    [ElementType.String]: OperatorUtil.getOptions([
      Operator.Contains,
      Operator.Equals,
      Operator.StartsWith,
      Operator.EndsWith,
      Operator.NotContains,
      Operator.NotEmpty,
      Operator.Empty,
    ]),
    [ElementType.Number]: OperatorUtil.getOptions(
      [
        Operator.Contains,
        Operator.Equals,
        Operator.NotEquals,
        Operator.Greater,
        Operator.GreaterOrEqual,
        Operator.Less,
        Operator.LessOrEqual,
        Operator.NotEmpty,
        Operator.Empty,
      ],
      true,
    ),
    [ElementType.Date]: OperatorUtil.getOptions([
      Operator.Equals,
      Operator.Range,
      Operator.Today,
      Operator.Yesterday,
      Operator.Last7Days,
      Operator.Last30Days,
      Operator.NotEmpty,
      Operator.Empty,
    ]),
    [ElementType.Boolean]: OperatorUtil.getOptions([Operator.True, Operator.False]),
    [ElementType.Enum]: OperatorUtil.getOptions([Operator.In, Operator.NotIn, Operator.NotEmpty, Operator.Empty]),
  };

  static getOptionsByType(type: ElementType) {
    return OperatorUtil.OPTIONS[type];
  }

  static getCurrentOption(tooltip: string | undefined) {
    return {
      code: CURRENT_OPERATOR,
      label: 'viewer.filter.current',
      tooltip,
    } as const;
  }

  static getListOptions<O extends Operator>(operators: O[], current: string | undefined) {
    const base = OperatorUtil.getOptions(operators);
    return current ? [...base, OperatorUtil.getCurrentOption(current)] : base;
  }
}
